import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { usePeriod, PresetPeriod } from '@/pages/reports/PeriodContext';
import { validateDateRange } from '@validators';

function getPresetDates(preset: Exclude<PresetPeriod, 'custom'>): { dateFrom: string; dateTo: string } {
  const today = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

  const todayStr = fmt(today);

  if (preset === 'current-month') {
    const first = new Date(today.getFullYear(), today.getMonth(), 1);
    return { dateFrom: fmt(first), dateTo: todayStr };
  }
  if (preset === 'last-3-months') {
    const from = new Date(today);
    from.setMonth(from.getMonth() - 3);
    return { dateFrom: fmt(from), dateTo: todayStr };
  }
  if (preset === 'last-6-months') {
    const from = new Date(today);
    from.setMonth(from.getMonth() - 6);
    return { dateFrom: fmt(from), dateTo: todayStr };
  }
  // current-year
  const first = new Date(today.getFullYear(), 0, 1);
  return { dateFrom: fmt(first), dateTo: todayStr };
}

const PRESETS: { label: string; value: PresetPeriod }[] = [
  { label: 'Current Month', value: 'current-month' },
  { label: 'Last 3 Months', value: 'last-3-months' },
  { label: 'Last 6 Months', value: 'last-6-months' },
  { label: 'Current Year', value: 'current-year' },
  { label: 'Custom Range', value: 'custom' },
];

export function PeriodSelector() {
  const { period, setPeriod } = usePeriod();
  const [customFrom, setCustomFrom] = useState(period.dateFrom);
  const [customTo, setCustomTo] = useState(period.dateTo);
  const [error, setError] = useState<string | null>(null);

  function handlePreset(preset: PresetPeriod) {
    setError(null);
    if (preset === 'custom') {
      setPeriod({ preset: 'custom', dateFrom: customFrom, dateTo: customTo });
      return;
    }
    const dates = getPresetDates(preset);
    setPeriod({ preset, ...dates });
  }

  function handleApplyCustom() {
    const result = validateDateRange(customFrom, customTo);
    if (!result.isValid) {
      setError(result.error ?? 'Invalid date range');
      return;
    }
    setError(null);
    setPeriod({ preset: 'custom', dateFrom: customFrom, dateTo: customTo });
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {PRESETS.map(({ label, value }) => (
        <Button
          key={value}
          variant={period.preset === value ? 'default' : 'outline'}
          size="sm"
          onClick={() => handlePreset(value)}
        >
          {label}
        </Button>
      ))}

      {period.preset === 'custom' && (
        <div className="flex flex-wrap items-center gap-2 mt-2 w-full">
          <Input
            type="date"
            aria-label="Start date"
            value={customFrom}
            onChange={(e) => { setCustomFrom(e.target.value); setError(null); }}
            className="w-40"
          />
          <span className="text-sm text-muted-foreground">to</span>
          <Input
            type="date"
            aria-label="End date"
            value={customTo}
            onChange={(e) => { setCustomTo(e.target.value); setError(null); }}
            className="w-40"
          />
          <Button size="sm" onClick={handleApplyCustom}>Apply</Button>
          {error && (
            <p role="alert" className="text-sm text-destructive w-full">{error}</p>
          )}
        </div>
      )}
    </div>
  );
}
